import { ShopAttributeDataType } from '@kanbu/schema/enums';

/**
 * The canonical-name fold, mirrored on the client so the add-a-value form can
 * tell an admin *before* they submit that the name they are typing is already
 * in the feed's vocabulary.
 *
 * It is a copy of the backend's `normalizeAttributeName` rather than a shared
 * import: that helper lives in `@kanbu/shared-backend`, which the admin app
 * does not depend on. The copy is safe because it is only ever advisory — the
 * server folds the name again and resolves the collision itself, so a drift
 * here can make the form's warning wrong but can never create a second row
 * for one name.
 *
 * Names stay in the source language, lowercased, diacritics stripped, and
 * everything that is not a letter or a digit collapsed to a single underscore.
 */
export function toCanonicalAttributeName(name: string): string {
  return name
    .normalize('NFD')
    .replaceAll(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replaceAll(/[^\p{Letter}\p{Number}]+/gu, '_')
    .replaceAll(/^_+|_+$/g, '');
}

export type ComparableNumber = {
  isValid: boolean;
  value: number | null;
};

/**
 * Reads the number a range filter compares this row on, out of the field the
 * admin typed it into.
 *
 * An empty field is a legitimate answer — `null` means this product is not
 * comparable to others on this attribute. Anything that does not parse is a
 * typo rather than an intention, so callers hold the save back instead of
 * storing `null` and silently dropping the row out of every range filter.
 *
 * A comma is accepted as the decimal separator because Czech admins type one;
 * nothing else is converted, and in particular no unit is parsed out of the
 * text. There is no conversion table any more, and inventing one for a single
 * hand-typed value is the ingest mistake this whole surface removed.
 */
export function parseComparableNumber(input: string): ComparableNumber {
  const trimmed = input.trim();

  if (!trimmed) {
    return { isValid: true, value: null };
  }

  const parsed = Number(trimmed.replace(',', '.'));

  return Number.isNaN(parsed)
    ? { isValid: false, value: null }
    : { isValid: true, value: parsed };
}

export type VocabularyAttribute = {
  name: string;
  aliases?: string[] | null;
};

/**
 * Finds the attribute a typed name already refers to: the canonical name
 * first, its aliases second.
 *
 * Aliases have to match, because enrichment maps them onto their attribute —
 * a new attribute claiming a name that is already an alias would split one
 * attribute's values across two rows. Canonical names win over aliases for the
 * same reason they do in the enrichment vocabulary: two attributes can
 * legitimately claim one alias, and the row that carries the name has to win.
 */
export function findVocabularyMatch<T extends VocabularyAttribute>({
  name,
  attributes,
}: {
  name: string;
  attributes: T[];
}): T | undefined {
  const canonicalName = toCanonicalAttributeName(name);

  if (!canonicalName) {
    return undefined;
  }

  return (
    attributes.find(attribute => attribute.name === canonicalName) ??
    attributes.find(attribute =>
      (attribute.aliases ?? []).some(
        alias => toCanonicalAttributeName(alias) === canonicalName,
      ),
    )
  );
}

/**
 * What a typed attribute name turned out to be. Everything but `reuse` is a
 * state the admin has to see before submitting rather than discover from a
 * failed save.
 */
export type VocabularyMatchState = 'none' | 'reuse' | 'taken' | 'inactive';

export type MatchableAttribute = {
  id: string;
  dataType: ShopAttributeDataType;
  active: boolean;
};

/**
 * Classifies the attribute a typed name resolved onto.
 *
 * `taken` and `inactive` are the two that would otherwise produce a value
 * nobody ever sees: a second row for one product and attribute, or a row under
 * a definition switched off across the whole feed.
 */
export function resolveMatchState({
  match,
  takenAttributeIds,
}: {
  match: MatchableAttribute | undefined;
  takenAttributeIds: Set<string>;
}): VocabularyMatchState {
  if (!match) {
    return 'none';
  }

  if (takenAttributeIds.has(match.id)) {
    return 'taken';
  }

  if (!match.active) {
    return 'inactive';
  }

  return 'reuse';
}

export type TypedAttribute = {
  dataType: ShopAttributeDataType;
  unit?: string | null;
  baseUnit?: string | null;
};

export type ValueTyping = {
  isNumeric: boolean;
  unitLabel: string;
};

/**
 * Which typing the value fields must follow: the resolved attribute's own,
 * whenever the name landed on one, and the draft's only while it is genuinely
 * new.
 *
 * An attribute that already exists is never re-typed — not by a later product
 * in enrichment, not by a re-import, and not from this form — so showing the
 * draft's type beside an existing definition would promise an edit that cannot
 * happen.
 */
export function resolveValueTyping({
  target,
  draftDataType,
  draftBaseUnit,
}: {
  target: TypedAttribute | undefined;
  draftDataType: ShopAttributeDataType;
  draftBaseUnit: string;
}): ValueTyping {
  if (target) {
    return {
      isNumeric: target.dataType === ShopAttributeDataType.Number,
      unitLabel: target.baseUnit ?? target.unit ?? '',
    };
  }

  return {
    isNumeric: draftDataType === ShopAttributeDataType.Number,
    unitLabel: draftBaseUnit.trim(),
  };
}

export type AddValueDraft = {
  mode: 'existing' | 'new';
  hasSelectedAttribute: boolean;
  matchState: VocabularyMatchState;
  draftName: string;
  draftBaseUnit: string;
  isNumeric: boolean;
  value: string;
  numericValue: string;
};

/**
 * Whether the form describes a value that can actually be written.
 *
 * Three rules earn their place here rather than in the markup:
 *
 * - a new **number** attribute needs its base unit, because a number stored
 *   under no stated unit is comparable to nothing and the typing cannot be
 *   corrected afterwards;
 * - a name that resolved onto something taken, switched off or tagged blocks
 *   the save, so the admin acts on the notice instead of getting a server
 *   error;
 * - a non-empty number that does not parse holds the save back, since storing
 *   `null` instead would silently drop the row out of every range filter.
 */
export function canAddValue({
  mode,
  hasSelectedAttribute,
  matchState,
  draftName,
  draftBaseUnit,
  isNumeric,
  value,
  numericValue,
}: AddValueDraft): boolean {
  if (!value.trim()) {
    return false;
  }

  if (isNumeric && !parseComparableNumber(numericValue).isValid) {
    return false;
  }

  if (mode === 'existing') {
    return hasSelectedAttribute;
  }

  if (matchState === 'reuse') {
    return true;
  }

  if (matchState !== 'none') {
    return false;
  }

  return !!draftName.trim() && (!isNumeric || !!draftBaseUnit.trim());
}
